// MIRESCO BIZ — opportunity catalog, permissions, role metadata
import {
  Briefcase, Building2, TrendingUp, Hammer, Megaphone,
  Users, MapPin, Store, Boxes, Truck, Coins, Handshake, Sparkles, ShoppingBag,
} from "lucide-react";

// 11 opportunity types
export const OPPORTUNITY_TYPES = [
  { value: "affiliate",           label: "Affiliate",            short: "Affiliate",    icon: Sparkles,    desc: "Promote a brand and earn commissions on sales." },
  { value: "agent",               label: "Agent",                short: "Agent",        icon: Briefcase,   desc: "Authorized representative for a brand or product." },
  { value: "vendor",              label: "Vendor",               short: "Vendor",       icon: ShoppingBag, desc: "Sell products under an established brand." },
  { value: "stockist",            label: "Stockist",             short: "Stockist",     icon: Boxes,       desc: "Hold and supply inventory in a region." },
  { value: "distributor",         label: "Distributor",          short: "Distributor",  icon: Truck,       desc: "Distribute products across a market or region." },
  { value: "kiosk",               label: "Kiosk",                short: "Kiosk",        icon: Store,       desc: "Run a branded kiosk outlet." },
  { value: "territory_partner",   label: "Territory Partner",    short: "Territory",    icon: MapPin,      desc: "Own a geographic territory exclusively." },
  { value: "contractor_job",      label: "Contractor Job",       short: "Contract",     icon: Hammer,      desc: "Project, service or job contract." },
  { value: "influencer_campaign", label: "Influencer Campaign",  short: "Influencer",   icon: Megaphone,   desc: "Paid social campaign for a brand." },
  { value: "investment",          label: "Investment Opportunity", short: "Investment", icon: Coins,       desc: "Capital deployment opportunity." },
  { value: "partnership",         label: "Strategic Partnership", short: "Partnership", icon: Handshake,   desc: "Joint venture or strategic co-build." },
];

export const POSTING_PERMISSIONS = {
  affiliate: ["business_owner"],
  agent: ["business_owner"],
  vendor: ["business_owner"],
  stockist: ["business_owner"],
  distributor: ["business_owner"],
  kiosk: ["business_owner"],
  territory_partner: ["business_owner"],
  contractor_job: ["business_owner"],
  influencer_campaign: ["business_owner"],
  investment: ["investor"],
  partnership: ["business_owner", "investor"],
};

export const APPLICATION_PERMISSIONS = {
  affiliate: ["entrepreneur"],
  agent: ["entrepreneur"],
  vendor: ["entrepreneur"],
  stockist: ["entrepreneur"],
  distributor: ["entrepreneur"],
  kiosk: ["entrepreneur"],
  territory_partner: ["entrepreneur"],
  contractor_job: ["contractor"],
  influencer_campaign: ["influencer"],
  investment: ["investor"],
  partnership: ["business_owner", "investor"],
};

export const ROLES = [
  {
    value: "entrepreneur",
    label: "Entrepreneur",
    icon: Briefcase,
    tagline: "Looking for business opportunities",
    desc: "Apply for agent, distributor, stockist, vendor, kiosk and territory opportunities.",
  },
  {
    value: "business_owner",
    label: "Business Owner",
    icon: Building2,
    tagline: "Looking for agents, stockists, distributors, vendors and partners",
    desc: "Post recruitment opportunities. Recruit affiliates, contractors, influencers.",
  },
  {
    value: "investor",
    label: "Investor",
    icon: TrendingUp,
    tagline: "Looking for investment and business opportunities",
    desc: "Post or explore investment rounds and strategic partnerships.",
  },
  {
    value: "contractor",
    label: "Contractor / Service Provider",
    icon: Hammer,
    tagline: "Looking for jobs and projects",
    desc: "Apply for contractor jobs and service contracts.",
  },
  {
    value: "influencer",
    label: "Influencer / Creator",
    icon: Megaphone,
    tagline: "Looking for brand collaborations and campaigns",
    desc: "List your audience, get discovered, and win paid campaigns.",
  },
];

export const ROLE_LABEL = Object.fromEntries(ROLES.map((r) => [r.value, r.label]));
export const TYPE_LABEL = Object.fromEntries(OPPORTUNITY_TYPES.map((t) => [t.value, t.label]));
export const TYPE_ICON = Object.fromEntries(OPPORTUNITY_TYPES.map((t) => [t.value, t.icon]));

export const TYPE_COLORS = {
  affiliate:           "bg-indigo-50 text-indigo-700 border-indigo-200",
  agent:               "bg-blue-50 text-[#C8102E] border-blue-200",
  vendor:              "bg-orange-50 text-orange-800 border-orange-200",
  stockist:            "bg-cyan-50 text-cyan-700 border-cyan-200",
  distributor:         "bg-teal-50 text-teal-700 border-teal-200",
  kiosk:               "bg-amber-50 text-amber-800 border-amber-200",
  territory_partner:   "bg-rose-50 text-rose-700 border-rose-200",
  contractor_job:      "bg-violet-50 text-violet-700 border-violet-200",
  influencer_campaign: "bg-pink-50 text-pink-700 border-pink-200",
  investment:          "bg-emerald-50 text-emerald-700 border-emerald-200",
  partnership:         "bg-zinc-100 text-zinc-800 border-zinc-300",
};

export function canPost(role, type, isAdmin = false) {
  if (isAdmin) return true;
  return (POSTING_PERMISSIONS[type] || []).includes(role);
}
export function canApply(role, type, isAdmin = false) {
  if (isAdmin) return true;
  return (APPLICATION_PERMISSIONS[type] || []).includes(role);
}
export function postableTypes(role, isAdmin = false) {
  if (isAdmin) return OPPORTUNITY_TYPES;
  return OPPORTUNITY_TYPES.filter((t) => POSTING_PERMISSIONS[t.value].includes(role));
}
export function applicableTypes(role, isAdmin = false) {
  if (isAdmin) return OPPORTUNITY_TYPES;
  return OPPORTUNITY_TYPES.filter((t) => APPLICATION_PERMISSIONS[t.value].includes(role));
}
